<template>
  <div id="lom-information-page">
    <!-- Loading Overlay -->
    <v-overlay :value="ProgressLoadingFlag">
      <v-progress-circular
        indeterminate
        size="60"
        color="primary"
      ></v-progress-circular>
    </v-overlay>

    <!-- Premium Page Header -->
    <v-card elevation="20" class="header-card mb-6 overflow-hidden">
      <div class="header-gradient">
        <div class="header-backdrop-effect"></div>
        <v-container fluid class="header-container">
          <v-row align="center" justify="space-between" class="ma-0">
            <!-- Title Section - Left -->
            <v-col cols="12" md="8" class="d-flex align-center gap-3">
              <div class="header-icon-box">
                <v-icon size="32" class="icon-white"
                  >mdi-information-outline</v-icon
                >
              </div>
              <div class="header-title-bar">
                <h1 class="header-title">Local Organization Information</h1>
                <p class="header-subtitle">{{ LomName }} - {{ LomCode }}</p>
              </div>
            </v-col>
          </v-row>
        </v-container>
      </div>
    </v-card>

    <v-card-text class="page-content">
      <!-- Information Cards Grid -->
      <v-container fluid class="pa-0">
        <v-row class="mb-6">
          <!-- Specimen Signature Card -->
          <v-col cols="12" lg="4" md="6" sm="12" class="mb-4">
            <v-card class="info-card ss-card-wrapper" elevation="8">
              <div class="card-header card-header--signatures">
                <div class="card-header-icon">
                  <v-icon>mdi-pen</v-icon>
                </div>
                <div class="card-header-content">
                  <h3 class="card-title">Specimen Signature Card</h3>
                  <p class="card-subtitle">Authorized signatories</p>
                </div>
              </div>
              <v-card-text class="card-body pt-4">
                <ss-card></ss-card>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- APP Document Card -->
          <v-col cols="12" lg="4" md="6" sm="12" class="mb-4">
            <custom-skeleton-loader :LoadingFlag="appSkeletonLoading" />
            <v-card
              v-if="!appSkeletonLoading"
              class="info-card app-card-wrapper"
              elevation="8"
            >
              <div class="card-header card-header--app">
                <div class="card-header-icon">
                  <v-icon>mdi-file-document-outline</v-icon>
                </div>
                <div class="card-header-content">
                  <h3 class="card-title">APP & Budget</h3>
                  <p class="card-subtitle">APP Document Status</p>
                </div>
              </div>

              <v-card-text class="card-body pt-4">
                <!-- Status Section -->
                <div class="status-section">
                  <div class="section-icon">
                    <v-icon>mdi-check-circle</v-icon>
                  </div>
                  <div class="section-content">
                    <div class="section-label">Document Status</div>
                    <v-chip
                      draggable
                      dark
                      small
                      :color="appDocument.LomDocumentStatusColor"
                      text-color="white"
                      class="status-chip"
                    >
                      {{
                        appDocumentResultFlag
                          ? appDocument.LomDocumentStatusTxt
                          : "Pending"
                      }}
                    </v-chip>
                  </div>
                  <v-btn
                    icon
                    class="refresh-btn"
                    @click="getAppDocuments"
                    :loading="appSkeletonLoading"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </div>
                <!-- Document Label -->
                <div class="document-label">
                  <v-icon small class="label-icon">mdi-file-document</v-icon>
                  <span>Annual Program Planner Document</span>
                </div>

                <!-- Document Section -->
                <div class="document-section">
                  <div
                    v-if="appDocument.LomDocumentFlag"
                    class="document-available"
                  >
                    <div class="document-row">
                      <div class="doc-icon success">
                        <v-icon small>mdi-check</v-icon>
                      </div>
                      <div class="doc-content">
                        <span class="doc-title">Document uploaded</span>
                        <span class="doc-subtitle">APP Document</span>
                      </div>
                      <v-btn
                        small
                        depressed
                        :href="appDocument.DocumentAssetPath"
                        target="_blank"
                        class="download-btn"
                      >
                        <v-icon small left>mdi-download</v-icon>
                        Download
                      </v-btn>
                    </div>
                  </div>

                  <div v-else class="document-empty">
                    <div class="document-row">
                      <div class="doc-icon pending">
                        <v-icon small>mdi-clock-outline</v-icon>
                      </div>
                      <div class="doc-content">
                        <span class="doc-title">No document uploaded</span>
                        <span class="doc-subtitle"
                          >Please upload your APP document</span
                        >
                      </div>
                      <v-btn
                        small
                        depressed
                        class="upload-btn"
                        @click.prevent="openAppDocumentUpload"
                      >
                        <v-icon small left>mdi-cloud-upload</v-icon>
                        Upload
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Accounts Statement Card -->
          <v-col cols="12" lg="4" md="6" sm="12" class="mb-4">
            <custom-skeleton-loader :LoadingFlag="accountsSkeletonLoading" />
            <v-card
              v-if="!accountsSkeletonLoading"
              class="info-card accounts-card-wrapper"
              elevation="8"
            >
              <div class="card-header card-header--accounts">
                <div class="card-header-icon">
                  <v-icon>mdi-file-chart</v-icon>
                </div>
                <div class="card-header-content">
                  <h3 class="card-title">Last Year Accounts</h3>
                  <p class="card-subtitle">Last Year Accounts Document</p>
                </div>
              </div>
              <v-card-text class="card-body pt-4">
                <!-- Status Section -->
                <div class="status-section">
                  <div class="section-icon">
                    <v-icon>mdi-check-circle</v-icon>
                  </div>
                  <div class="section-content">
                    <div class="section-label">Document Status</div>
                    <v-chip
                      draggable
                      dark
                      small
                      :color="accountsDocument.LomDocumentStatusColor"
                      text-color="white"
                      class="status-chip"
                    >
                      {{
                        accountsDocumentResultFlag
                          ? accountsDocument.LomDocumentStatusTxt
                          : "Pending"
                      }}
                    </v-chip>
                  </div>
                  <v-btn
                    icon
                    class="refresh-btn"
                    @click="getAccountsDocuments"
                    :loading="accountsSkeletonLoading"
                  >
                    <v-icon>mdi-refresh</v-icon>
                  </v-btn>
                </div>
                <!-- Document Label -->
                <div class="document-label">
                  <v-icon small class="label-icon">mdi-file-document</v-icon>
                  <span>Last Year Accounts Document</span>
                </div>

                <!-- Document Section -->
                <div class="document-section">
                  <div
                    v-if="accountsDocument.LomDocumentFlag"
                    class="document-available"
                  >
                    <div class="document-row">
                      <div class="doc-icon success">
                        <v-icon small>mdi-check</v-icon>
                      </div>
                      <div class="doc-content">
                        <span class="doc-title">Document uploaded</span>
                        <span class="doc-subtitle"
                          >Last Year Accounts Document</span
                        >
                      </div>
                      <v-btn
                        small
                        depressed
                        :href="accountsDocument.DocumentAssetPath"
                        target="_blank"
                        class="download-btn"
                      >
                        <v-icon small left>mdi-download</v-icon>
                        Download
                      </v-btn>
                    </div>
                  </div>

                  <div v-else class="document-empty">
                    <div class="document-row">
                      <div class="doc-icon pending">
                        <v-icon small>mdi-clock-outline</v-icon>
                      </div>
                      <div class="doc-content">
                        <span class="doc-title">No document uploaded</span>
                        <span class="doc-subtitle"
                          >Please upload last year's accounts statement</span
                        >
                      </div>
                      <v-btn
                        small
                        depressed
                        class="upload-btn"
                        @click.prevent="openAccountsDocumentUpload"
                      >
                        <v-icon small left>mdi-cloud-upload</v-icon>
                        Upload
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <!-- LO Affiliation Status Card -->
          <v-col cols="12" lg="4" md="6" sm="12" class="mb-4">
            <v-card class="info-card affiliation-card-wrapper" elevation="8">
              <div class="card-header card-header--affiliation">
                <div class="card-header-icon">
                  <v-icon>mdi-file-document</v-icon>
                </div>
                <div class="card-header-content">
                  <h3 class="card-title">Affiliation Status</h3>
                  <p class="card-subtitle">Document verification</p>
                </div>
              </div>
              <v-card-text class="card-body pt-4">
                <affiliation-document-list></affiliation-document-list>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- LO Votes Card -->
          <v-col cols="12" lg="4" md="6" sm="12" class="mb-4">
            <v-card class="info-card voting-card-wrapper" elevation="8">
              <div class="card-header card-header--voting">
                <div class="card-header-icon">
                  <v-icon>mdi-hand-right</v-icon>
                </div>
                <div class="card-header-content">
                  <h3 class="card-title">Voting Rights</h3>
                  <p class="card-subtitle">Vote count</p>
                </div>
              </div>
              <v-card-text class="card-body pt-4">
                <voting-count></voting-count>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Location Details Card -->
          <v-col cols="12" lg="4" md="6" sm="12" class="mb-4">
            <v-card class="info-card location-card-wrapper" elevation="8">
              <div class="card-header card-header--location">
                <div class="card-header-icon">
                  <v-icon>mdi-map-marker</v-icon>
                </div>
                <div class="card-header-content">
                  <h3 class="card-title">Location Details</h3>
                  <p class="card-subtitle">Address information</p>
                </div>
              </div>
              <v-card-text class="card-body pt-4">
                <location-information-list></location-information-list>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Bank Details Card -->
          <v-col cols="12" lg="4" md="6" sm="12" class="mb-4">
            <v-card class="info-card bank-details-card-wrapper" elevation="8">
              <div class="card-header card-header--bank-details">
                <div class="card-header-icon">
                  <v-icon>mdi-bank</v-icon>
                </div>
                <div class="card-header-content">
                  <h3 class="card-title">Bank Details</h3>
                  <p class="card-subtitle">Financial account information</p>
                </div>
              </div>
              <v-card-text class="card-body pt-4">
                <bank-information-list
                  :isStandalone="false"
                ></bank-information-list>
              </v-card-text>
            </v-card>
          </v-col>
          <!-- First ZVP Visit Card -->
          <v-col cols="12" lg="4" md="6" sm="12" class="mb-4">
            <v-card class="info-card ldmt-card-wrapper" elevation="8">
              <div class="card-header card-header--ldmt">
                <div class="card-header-icon">
                  <v-icon>mdi-calendar-check</v-icon>
                </div>
                <div class="card-header-content">
                  <h3 class="card-title">ZVP First half visit</h3>
                  <p class="card-subtitle">First Half Status</p>
                </div>
              </div>
              <v-card-text class="card-body pt-4">
                <ldmt-status></ldmt-status>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- Second ZVP Visit Card -->
          <v-col cols="12" lg="4" md="6" sm="12" class="mb-4">
            <v-card class="info-card lav-card-wrapper" elevation="8">
              <div class="card-header card-header--lav">
                <div class="card-header-icon">
                  <v-icon>mdi-calendar-star</v-icon>
                </div>
                <div class="card-header-content">
                  <h3 class="card-title">ZVP Second half visit</h3>
                  <p class="card-subtitle">Second Half Status</p>
                </div>
              </div>
              <v-card-text class="card-body pt-4">
                <lav-status></lav-status>
              </v-card-text>
            </v-card>
          </v-col>

          <!-- LO Rename Request Card -->
          <v-col cols="12" lg="4" md="6" sm="12" class="mb-4">
            <v-card class="info-card rename-card-wrapper" elevation="8">
              <div class="card-header card-header--rename">
                <span class="new-feature-badge">New Feature</span>
                <div class="card-header-icon">
                  <v-icon>mdi-pencil-circle-outline</v-icon>
                </div>
                <div class="card-header-content">
                  <h3 class="card-title">LO Rename Request</h3>
                  <p class="card-subtitle">
                    Request a new name for your Local Organization
                  </p>
                </div>
              </div>
              <v-card-text class="card-body pt-4">
                <div class="rename-details-content">
                  <div class="info-row">
                    <span class="info-label">Current LO Name</span>
                    <span class="info-value">{{
                      LomName || "Loading..."
                    }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">LO Code</span>
                    <span class="info-value">{{
                      LomCode || "Loading..."
                    }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">State</span>
                    <span class="info-value">{{
                      StateName || "Loading..."
                    }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">District</span>
                    <span class="info-value">{{
                      DistrictName || "Loading..."
                    }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-label">City</span>
                    <span class="info-value">{{
                      CityName || "Loading..."
                    }}</span>
                  </div>
                  <div class="info-row" v-if="LomStatusText">
                    <span class="info-label">LO Status</span>
                    <span class="info-value">
                      <v-chip
                        x-small
                        label
                        :color="LomStatusColor || 'primary'"
                        text-color="white"
                        class="px-2 font-weight-bold"
                      >
                        {{ LomStatusText }}
                      </v-chip>
                    </span>
                  </div>

                  <!-- <v-btn
                    block
                    depressed
                    large
                    class="request-rename-btn mt-4"
                    @click="$router.push('/lo-rename-order/create')"
                  >
                    Request LO Name Change
                    <v-icon right>mdi-arrow-right-circle</v-icon>
                  </v-btn> -->

                  <v-btn
                    block
                    depressed
                    large
                    disabled
                    class="request-rename-btn mt-4"
                  >
                    LO Name Change (Coming Soon)
                  </v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <!-- Add LOM Document Popup -->
    <v-dialog v-model="addLomDocumentPrompt" persistent max-width="70%">
      <add-lom-document
        v-if="addLomDocumentPrompt"
        :addRecordPrompt="addLomDocumentPrompt"
        :pageTitle="documentUploadTitle"
        :pageDescription="documentUploadDescription"
        :recordData="documentUploadData"
        :UserInterface="UserInterface"
        @hideAddRecordPrompt="hideAddLomDocumentPrompt"
      ></add-lom-document>
    </v-dialog>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";
import LocationInformationList from "@/view/pages/erp/lom/lom-information/LocationInformationList.vue";
import BankInformationList from "@/view/pages/erp/lom/lom-information/BankInformationList.vue";
import AffiliationDocumentList from "@/view/pages/erp/lom/affilication-document/AffiliationDocumentList.vue";
import SsCard from "@/view/pages/erp/lom/lom-information/SsCard.vue";
import VotingCount from "@/view/pages/erp/lom/lom-information/VotingCount.vue";
import LdmtStatus from "@/view/pages/erp/lom/lom-information/LdmtStatus.vue";
import LavStatus from "@/view/pages/erp/lom/lom-information/LavStatus.vue";
import AddLomDocument from "@/view/pages/erp/reports/AddLomDocument.vue";

export default {
  mixins: [common],
  components: {
    BankInformationList,
    LocationInformationList,
    AffiliationDocumentList,
    SsCard,
    VotingCount,
    LdmtStatus,
    LavStatus,
    AddLomDocument,
  },
  data() {
    return {
      ProgressLoadingFlag: false,
      VotingRightsStatus: "",
      DocumentPath: "",
      uploadDocumentPrompt: false,
      EditRecordPrompt: false,
      presidentDatePrompt: false,
      lomInformationStatus: false,

      EligibleCount: 0,
      selectedData: {},
      VotingData: {},

      LdmtCardFlag: true,
      LavCardFlag: false,

      LomName: "",
      LomCode: "",

      addLomDocumentPrompt: false,
      documentUploadTitle: "",
      documentUploadDescription: "",
      documentUploadData: {},
      currentDocumentType: "",
      UserInterface: 0,
      // APP Document Data
      appDocument: {
        LomDocumentStatusTxt: "Loading...",
        LomDocumentStatusColor: "#3A67B1",
        LomDocumentFlag: false,
        DocumentAssetPath: "",
      },
      appSkeletonLoading: false,
      appDocumentResultFlag: false,

      // Accounts Document Data
      accountsDocument: {
        LomDocumentStatusTxt: "Loading...",
        LomDocumentStatusColor: "#3A67B1",
        LomDocumentFlag: false,
        DocumentAssetPath: "",
      },
      accountsSkeletonLoading: false,
      accountsDocumentResultFlag: false,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      JciYearCode: "",
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
      StateName: "",
      DistrictName: "",
      CityName: "",
      LomStatusText: "",
      LomStatusColor: "",
      sessionMember: null,
    };
  },
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.ProgressLoadingFlag = false;
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });
        this.JciYearCode = CurrentJciYearId;

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        var NextJciYearId = parseInt(tr.NextJciYearId);
        console.log({ NextJciYearId });
        this.NextYearCode = NextJciYearId;

        var NextJciYearName = parseInt(tr.NextJciYearName);
        console.log({ NextJciYearName });
        this.NextYearName = NextJciYearName;

        this.CurrentYearId = NextJciYearId;

        var LomId = this.$session.get("LomId");
        LomId = LomId == (null || undefined) ? "" : LomId;
        this.LomCode = LomId;

        var LomName = this.$session.get("LomName");
        LomName = LomName == (null || undefined) ? "" : LomName;
        console.log({ LomName });
        this.LomName = LomName;

        var server_url = companyConfig.apiURL;
        console.log("server_url = " + server_url);
        var SampleFilePath = server_url + "assets/sample/voting_rights.pdf";
        console.log("SampleFilePath = " + SampleFilePath);
        this.SampleFilePath = SampleFilePath;
      }
    },
  },
  methods: {
    backPage() {
      this.$router.go(-1);
    },
    refreshPageData() {
      console.log("refreshPageData called");
      this.ProgressLoadingFlag = true;
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "voting_rights",
        Action: "list",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
      this.pageData();
    },
    pageData() {
      console.log("pageData called");
      // this.getTableRecords();
      this.getAppDocuments();
      this.getAccountsDocuments();
    },
    hideUploadRecordPrompt(flag) {
      console.log("hideUploadRecordPrompt called");
      this.uploadDocumentPrompt = false;
      if (flag) {
        this.$emit("hidePreviewRecordPrompt", true);
      }
    },
    clickPresidentDate() {
      console.log("clickDate called");
      var tr = this.Records;
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.presidentDatePrompt = true;
        this.rowData = tr;
      } else {
        var message = "Selected Date failed";
        this.toast("error", message);
      }
    },
    approvedStatus() {
      console.log("clickDate called");
      var tr = this.Records;
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));
      if (n1 > 0) {
        this.lomInformationStatus = true;
        this.selectedData = tr;
      } else {
        var message = "Votingdescription failed";
        this.toast("error", message);
      }
    },
    hideUploadRecordPrompt() {
      console.log("hideUploadRecordPrompt called");
      this.uploadDocumentPrompt = false;
      // this.getTableRecords();
    },
    closePrompt() {
      console.log("closePrompt called");
      this.EditRecordPrompt = false;
    },
    editData() {
      console.log("editData called");
      var tr = this.VotingData;
      var n1 = Object.keys(tr).length;
      console.log("n1=" + n1 + ", tr=" + JSON.stringify(tr));

      if (n1 > 0) {
        this.EditRecordPrompt = true;
        this.selectedData = tr;
      } else {
        var message = "Votingdescription failed";
        this.toast("error", message);
      }
    },
    openAppDocumentUpload() {
      console.log("openAppDocumentUpload called");
      this.currentDocumentType = "APP";
      this.documentUploadTitle = "Upload Annual Program Planner (APP)";
      this.documentUploadDescription =
        "Upload your Annual Program Planner document";
      this.UserInterface = 6;
      this.addLomDocumentPrompt = true;
    },
    openAccountsDocumentUpload() {
      console.log("openAccountsDocumentUpload called");
      this.currentDocumentType = "ACCOUNTS";
      this.documentUploadTitle = "Upload Last Year Accounts Statement";
      this.documentUploadDescription =
        "Upload your last year's accounts statement";
      this.UserInterface = 5;
      this.addLomDocumentPrompt = true;
    },
    getAppDocuments() {
      console.log("getAppDocuments called");

      this.appSkeletonLoading = true;
      this.appDocument = {
        LomDocumentStatusTxt: "Pending",
        LomDocumentStatusColor: "#EDBE38",
        LomDocumentFlag: false,
        DocumentAssetPath: "",
      };
      this.appDocumentResultFlag = false;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/lom-document/show";
      var upload = {
        UserInterface: 6,
      };
      console.log("APP upload=" + JSON.stringify(upload));

      var thisIns = this;
      var output = "";
      var flag = false;

      this.$http({
        method: "post",
        url: add_url,
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          console.log("APP Documents Response:", response.data);
          thisIns.appSkeletonLoading = false;

          output = response.data.output;
          flag = response.data.flag;
          var records = response.data.records;

          if (records && records.StateName)
            thisIns.StateName = records.StateName;
          if (records && records.DistrictName)
            thisIns.DistrictName = records.DistrictName;
          if (records && records.CityName) thisIns.CityName = records.CityName;
          if (records && records.LomStatusText)
            thisIns.LomStatusText = records.LomStatusText;
          if (records && records.LomStatusColor)
            thisIns.LomStatusColor = records.LomStatusColor;
          if (response.data.member)
            thisIns.sessionMember = response.data.member;

          if (flag && records) {
            thisIns.appDocumentResultFlag = true;
            thisIns.appDocument = records;
          } else {
            thisIns.appDocumentResultFlag = false;
            thisIns.appDocument = {
              LomDocumentStatusTxt: "Pending",
              LomDocumentStatusColor: "#EDBE38",
              LomDocumentFlag: false,
              DocumentAssetPath: "",
            };
          }
        })
        .catch(function (error) {
          console.error("APP Documents Error:", error);
          thisIns.appSkeletonLoading = false;
          thisIns.appDocumentResultFlag = false;
        });
    },
    getAccountsDocuments() {
      console.log("getAccountsDocuments called");

      this.accountsSkeletonLoading = true;
      this.accountsDocument = {
        LomDocumentStatusTxt: "Pending",
        LomDocumentStatusColor: "#EDBE38",
        LomDocumentFlag: false,
        DocumentAssetPath: "",
      };
      this.accountsDocumentResultFlag = false;

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/lom-document/show";
      var upload = {
        UserInterface: 5,
      };
      console.log("Accounts upload=" + JSON.stringify(upload));

      var thisIns = this;
      var output = "";
      var flag = false;

      this.$http({
        method: "post",
        url: add_url,
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      })
        .then(function (response) {
          console.log("Accounts Documents Response:", response.data);
          thisIns.accountsSkeletonLoading = false;

          output = response.data.output;
          flag = response.data.flag;
          var records = response.data.records;

          if (records && records.StateName)
            thisIns.StateName = records.StateName;
          if (records && records.DistrictName)
            thisIns.DistrictName = records.DistrictName;
          if (records && records.CityName) thisIns.CityName = records.CityName;
          if (records && records.LomStatusText)
            thisIns.LomStatusText = records.LomStatusText;
          if (records && records.LomStatusColor)
            thisIns.LomStatusColor = records.LomStatusColor;
          if (response.data.member)
            thisIns.sessionMember = response.data.member;

          if (flag && records) {
            thisIns.accountsDocumentResultFlag = true;
            thisIns.accountsDocument = records;
          } else {
            thisIns.accountsDocumentResultFlag = false;
            thisIns.accountsDocument = {
              LomDocumentStatusTxt: "Pending",
              LomDocumentStatusColor: "#EDBE38",
              LomDocumentFlag: false,
              DocumentAssetPath: "",
            };
          }
        })
        .catch(function (error) {
          console.error("Accounts Documents Error:", error);
          thisIns.accountsSkeletonLoading = false;
          thisIns.accountsDocumentResultFlag = false;
        });
    },
    hideAddLomDocumentPrompt(flag) {
      console.log("hideAddLomDocumentPrompt called");
      this.addLomDocumentPrompt = false;
      if (flag) {
        this.toast("success", "Document uploaded successfully");
        this.refreshPageData();
      }
    },
  },
  mounted() {},
  beforeMount() {
    this.refreshPageData();
  },
};
</script>
<style lang="scss" scoped>
// Color Palette (JCI Approved Colors)
$navy: #3a67b1;
$aqua: #0097d7;
$gold: #edbe38;
$seaform: #56bda3;
$text-90-black: #414042;
$text-off-black: #0a0f29;
$text-white: #ffffff;
$signature-color: $navy;
$affiliation-color: $seaform;
$voting-color: $navy;
$location-color: $aqua;
$ldmt-color: $navy;
$lav-color: $seaform;

$primary-color: $navy;
$secondary-color: $aqua;
$success-color: $seaform;
$warning-color: $gold;
$danger-color: #e74c3c;
$info-color: $aqua;
$light-bg: #f8fafc;
$bg-light: #f1f5f9;
$text-primary: $text-off-black;
$text-secondary: $text-90-black;
$border-color: #e2e8f0;

// Animations
@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }

  50% {
    transform: translateY(-12px);
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

#lom-information-page {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  padding: 30px 15px;

  // ============================================
  // HEADER CARD
  // ============================================
  .header-card {
    background: linear-gradient(135deg, $navy 0%, $aqua 100%) !important;
    border-radius: 20px;
    overflow: hidden;
    animation: slideDown 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
    position: relative;
    border: none;
    box-shadow: 0 20px 60px rgba(58, 103, 177, 0.5),
      0 0 30px rgba(0, 151, 215, 0.3);

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: float 8s ease-in-out infinite;
    }
  }
  .document-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: #414042;
    padding: 0 4px;

    .label-icon {
      color: #3a67b1;
    }
  }
  .header-backdrop-effect {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: 0.5;
  }

  .header-container {
    padding: 20px 0 !important;
  }

  .header-icon-box {
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.25) 0%,
      rgba(255, 255, 255, 0.15) 100%
    );
    backdrop-filter: blur(15px);
    border-radius: 18px;
    padding: 14px;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.4);
    animation: float 4s ease-in-out infinite;
    border: 2px solid rgba(255, 255, 255, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;

    .icon-white {
      color: $text-white !important;
    }
  }

  .header-title-bar {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .header-title {
    font-size: 36px;
    font-weight: 900;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.3), 0 0 20px rgba(0, 151, 215, 0.3);
    animation: slideInLeft 0.8s ease-out;
    letter-spacing: 0.8px;
    color: $text-white;
    margin: 0;
  }

  .header-subtitle {
    font-size: 17px;
    opacity: 0.98;
    animation: slideInLeft 1s ease-out 0.1s backwards;
    letter-spacing: 0.4px;
    color: $text-white;
    font-weight: 500;
    margin: 0;
  }

  .gap-3 {
    gap: 1rem !important;
  }

  .action-btn {
    background: rgba(255, 255, 255, 0.15) !important;
    border: 2px solid rgba(255, 255, 255, 0.3) !important;
    backdrop-filter: blur(10px);
    color: white !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background: rgba(255, 255, 255, 0.25) !important;
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
    }
  }

  // ============================================
  // PAGE CONTENT
  // ============================================
  .page-content {
    padding: 0;
  }

  // ============================================
  // INFO CARDS
  // ============================================
  .info-card {
    border-radius: 18px !important;
    border: 2px solid rgba(58, 103, 177, 0.15) !important;
    box-shadow: 0 12px 40px rgba(58, 103, 177, 0.15),
      0 0 30px rgba(0, 151, 215, 0.08);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    animation: slideInUp 0.6s ease-out;
    background: linear-gradient(135deg, #ffffff 0%, #fafbff 100%);
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: radial-gradient(
        circle at 20% 50%,
        rgba(58, 103, 177, 0.05) 0%,
        transparent 50%
      );
      pointer-events: none;
    }

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 60px rgba(58, 103, 177, 0.2),
        0 0 40px rgba(0, 151, 215, 0.15);
      border-color: rgba(0, 151, 215, 0.3) !important;
    }
  }

  .card-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    border-radius: 16px 16px 0 0;
    background: linear-gradient(135deg, $navy 0%, $aqua 100%);
    position: relative;
    overflow: hidden;

    &::before {
      content: "";
      position: absolute;
      top: -50%;
      right: -50%;
      width: 200%;
      height: 200%;
      background: radial-gradient(
        circle,
        rgba(255, 255, 255, 0.1) 0%,
        transparent 70%
      );
      animation: float 6s ease-in-out infinite;
    }
  }

  .card-header--signatures {
    background: linear-gradient(135deg, $navy 0%, lighten($navy, 10%) 100%);
  }

  .card-header--affiliation {
    background: linear-gradient(
      135deg,
      $seaform 0%,
      darken($seaform, 10%) 100%
    );
  }

  .card-header--voting {
    background: linear-gradient(135deg, $gold 0%, darken($gold, 15%) 100%);
  }

  .card-header--location {
    background: linear-gradient(135deg, $aqua 0%, lighten($aqua, 10%) 100%);
  }

  .card-header--ldmt {
    background: linear-gradient(135deg, $navy 0%, $aqua 100%);
  }

  .card-header--lav {
    background: linear-gradient(135deg, $seaform 0%, $aqua 100%);
  }

  .card-header--app {
    background: linear-gradient(135deg, $gold 0%, darken($gold, 15%) 100%);
  }

  .card-header--accounts {
    background: linear-gradient(
      135deg,
      $seaform 0%,
      darken($seaform, 10%) 100%
    );
  }

  .card-header--bank-details {
    background: linear-gradient(135deg, $aqua 0%, lighten($aqua, 10%) 100%);
  }

  // APP and Accounts Card Styles (matching SsCard pattern)
  .app-card-wrapper,
  .accounts-card-wrapper {
    .status-section {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 14px;
      background: linear-gradient(
        135deg,
        rgba(58, 103, 177, 0.08) 0%,
        rgba(58, 103, 177, 0.05) 100%
      );
      border-radius: 10px;
      border-left: 3px solid #3a67b1;
    }

    .section-icon {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      background: #3a67b1;
      border-radius: 8px;
      color: white;

      .v-icon {
        font-size: 18px;
      }
    }

    .section-content {
      flex: 1;
    }

    .section-label {
      font-size: 10px;
      font-weight: 600;
      color: #414042;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin-bottom: 4px;
    }

    .status-chip {
      font-weight: 600 !important;
      font-size: 11px !important;
      height: 22px !important;
    }

    .refresh-btn {
      margin-left: auto;
      color: #3a67b1 !important;
      width: 28px !important;
      height: 28px !important;
      transition: all 0.3s ease;

      .v-icon {
        font-size: 18px;
      }

      &:hover {
        background: rgba(58, 103, 177, 0.1) !important;
        transform: rotate(180deg);
      }
    }

    .document-section {
      padding: 0;
    }

    .document-row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 16px;
      border-radius: 10px;
    }

    .doc-icon {
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 2px solid;

      &.success {
        background: rgba(76, 175, 80, 0.1);
        border-color: #4caf50;
        color: #4caf50;
      }

      &.pending {
        background: rgba(237, 190, 56, 0.1);
        border-color: #edbe38;
        color: #edbe38;
      }
    }

    .doc-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;

      .doc-title {
        font-size: 13px;
        font-weight: 600;
        color: #0a0f29;
      }

      .doc-subtitle {
        font-size: 11px;
        color: #666;
      }
    }

    .document-available {
      .document-row {
        background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
        border: 1px solid rgba(76, 175, 80, 0.3);
      }
    }

    .document-empty {
      .document-row {
        background: linear-gradient(135deg, #fff9c4 0%, #fffde7 100%);
        border: 1px solid rgba(237, 190, 56, 0.4);
      }
    }

    .download-btn {
      background: linear-gradient(
        135deg,
        #3a67b1 0%,
        darken(#3a67b1, 10%) 100%
      ) !important;
      color: white !important;
      padding: 0 12px !important;
      height: 30px !important;
      border-radius: 6px !important;
      font-weight: 600 !important;
      font-size: 11px !important;
      text-transform: none !important;
      letter-spacing: 0 !important;
      transition: all 0.3s ease !important;

      &:hover {
        box-shadow: 0 4px 12px rgba(58, 103, 177, 0.3);
      }
    }

    .upload-btn {
      background: linear-gradient(
        135deg,
        #edbe38 0%,
        darken(#edbe38, 10%) 100%
      ) !important;
      color: white !important;
      padding: 0 12px !important;
      height: 30px !important;
      border-radius: 6px !important;
      font-weight: 600 !important;
      font-size: 11px !important;
      text-transform: none !important;
      letter-spacing: 0 !important;
      transition: all 0.3s ease !important;

      &:hover {
        box-shadow: 0 4px 12px rgba(237, 190, 56, 0.3);
      }
    }
  }

  .card-header-icon {
    background: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    padding: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px solid rgba(255, 255, 255, 0.3);
    flex-shrink: 0;
    position: relative;
    z-index: 1;

    .v-icon {
      color: $text-white !important;
      font-size: 24px;
    }
  }

  .card-header-content {
    flex: 1;
    position: relative;
    z-index: 1;
  }

  .card-title {
    font-size: 18px;
    font-weight: 700;
    color: $text-white;
    margin: 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  .card-subtitle {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.9);
    margin: 4px 0 0 0;
    font-weight: 500;
    opacity: 0.95;
  }

  .card-body {
    padding: 20px !important;
    position: relative;
    z-index: 1;
  }

  .app-card-wrapper .card-body,
  .accounts-card-wrapper .card-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px !important;
  }

  // ============================================
  // RESPONSIVE DESIGN
  // ============================================
  @media (max-width: 960px) {
    .header-card {
      margin-bottom: 20px !important;
    }

    .header-title {
      font-size: 28px !important;
    }

    .header-subtitle {
      font-size: 14px !important;
    }
  }

  @media (max-width: 600px) {
    padding: 15px;

    .header-card {
      margin-bottom: 15px !important;
    }

    .header-title {
      font-size: 24px !important;
    }

    .header-subtitle {
      font-size: 12px !important;
    }

    .info-card {
      margin-bottom: 12px !important;
    }

    .card-title {
      font-size: 16px !important;
    }

    .card-subtitle {
      font-size: 12px !important;
    }
  }

  .rename-card-wrapper {
    position: relative;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 60px rgba(58, 103, 177, 0.2),
        0 0 40px rgba(0, 151, 215, 0.15) !important;
    }

    &::after {
      content: "";
      position: absolute;
      bottom: -30px;
      right: -30px;
      width: 120px;
      height: 120px;
      background: radial-gradient(
        circle,
        rgba(58, 103, 177, 0.05) 0%,
        transparent 70%
      );
      border-radius: 50%;
      pointer-events: none;
      z-index: 0;
    }

    &::before {
      content: "";
      position: absolute;
      top: 20%;
      left: -20px;
      width: 60px;
      height: 60px;
      background: radial-gradient(
        circle,
        rgba(86, 189, 163, 0.04) 0%,
        transparent 70%
      );
      border-radius: 50%;
      pointer-events: none;
      z-index: 0;
    }
  }

  .card-header--rename {
    background: linear-gradient(
      135deg,
      #3a67b1 0%,
      #0097d7 50%,
      #56bda3 100%
    ) !important;
    position: relative;
    overflow: hidden;

    .new-feature-badge {
      position: absolute;
      top: 12px;
      right: 12px;
      background: rgba(255, 255, 255, 0.2);
      backdrop-filter: blur(4px);
      color: white;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      padding: 3px 8px;
      border-radius: 20px;
      border: 1px solid rgba(255, 255, 255, 0.4);
      letter-spacing: 0.5px;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
      z-index: 2;
    }
  }

  .rename-details-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
    z-index: 1;

    .info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 14px;
      background: linear-gradient(
        135deg,
        rgba(58, 103, 177, 0.04) 0%,
        rgba(0, 151, 215, 0.02) 100%
      );
      border-radius: 10px;
      border-left: 3px solid #3a67b1;
      transition: all 0.2s ease;

      &:hover {
        background: linear-gradient(
          135deg,
          rgba(58, 103, 177, 0.08) 0%,
          rgba(0, 151, 215, 0.04) 100%
        );
        transform: translateX(3px);
      }

      .info-label {
        font-size: 12px;
        font-weight: 600;
        color: #718096;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .info-value {
        font-size: 14px;
        font-weight: 700;
        color: #2d3748;
      }
    }

    .request-rename-btn {
      background: linear-gradient(
        135deg,
        #3a67b1 0%,
        #0097d7 50%,
        #56bda3 100%
      ) !important;
      color: white !important;
      font-weight: 700 !important;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      border-radius: 8px;
      height: 48px !important;
      box-shadow: 0 4px 15px rgba(58, 103, 177, 0.2) !important;
      transition: all 0.3s ease !important;

      .v-icon {
        transition: transform 0.3s ease;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(58, 103, 177, 0.35) !important;

        .v-icon {
          transform: translateX(4px);
        }
      }

      &:active {
        transform: translateY(1px);
      }
    }
  }
}
</style>